'use client';

import { useMemo, useState } from 'react';
import {
  DEFAULT_OBFUSCATE_OPTIONS,
  OBFUSCATE_SAMPLE,
  obfuscateJs,
  type ObfuscateOptions,
} from '@/lib/browser-jsobfuscate-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function BrowserJavascriptObfuscator() {
  const [input, setInput] = useState<string>(OBFUSCATE_SAMPLE);
  const [options, setOptions] = useState<ObfuscateOptions>(DEFAULT_OBFUSCATE_OPTIONS);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const { output, error } = useMemo(() => {
    try {
      return { output: obfuscateJs(input, options), error: null as string | null };
    } catch {
      return {
        output: '',
        error: 'The code could not be processed. Check for unusual syntax and try again.',
      };
    }
  }, [input, options]);

  function update<K extends keyof ObfuscateOptions>(key: K, value: ObfuscateOptions[K]) {
    setOptions((prev) => ({ ...prev, [key]: value }));
  }

  async function handleCopy() {
    try {
      await copyText(output);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">JavaScript</span>
        <h2 className="tool-live-title">JavaScript obfuscator</h2>
        <p className="tool-live-subtitle">
          Rename local variables, encode string literals, add junk code, and strip whitespace to
          make JavaScript harder to read. This is light obfuscation, not real protection, and it can
          be reversed.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>JavaScript</span>
              <textarea
                onChange={(e) => setInput(e.target.value)}
                rows={14}
                spellCheck={false}
                value={input}
              />
            </label>
            <label className="tool-live-field">
              <span>Encode strings</span>
              <select
                onChange={(e) =>
                  update('encodeStrings', e.target.value as ObfuscateOptions['encodeStrings'])
                }
                value={options.encodeStrings}
              >
                <option value="none">None</option>
                <option value="hex">Hex escapes (\xNN)</option>
                <option value="base64">Base64 + decoder</option>
              </select>
            </label>
            <label
              className="tool-live-field"
              style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
            >
              <input
                checked={options.renameLocals}
                onChange={(e) => update('renameLocals', e.target.checked)}
                type="checkbox"
              />
              <span>Rename local identifiers</span>
            </label>
            <label
              className="tool-live-field"
              style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
            >
              <input
                checked={options.insertDeadCode}
                onChange={(e) => update('insertDeadCode', e.target.checked)}
                type="checkbox"
              />
              <span>Insert dead code</span>
            </label>
            <label
              className="tool-live-field"
              style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
            >
              <input
                checked={options.minify}
                onChange={(e) => update('minify', e.target.checked)}
                type="checkbox"
              />
              <span>Minify whitespace</span>
            </label>
          </div>

          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleCopy} type="button">
              Copy result
            </button>
          </div>
          {error ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {error}
            </p>
          ) : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <label className="tool-live-field tool-live-field-full">
            <span>Obfuscated JavaScript</span>
            <textarea readOnly rows={18} spellCheck={false} value={output} />
          </label>
          <p className="tool-live-panel-note">
            Processed locally in your browser. Obfuscation deters casual reading only, it does not
            secure your code.
          </p>
        </section>
      </div>
    </div>
  );
}
